// OKX public WebSocket singleton — wss://ws.okx.com:8443/ws/v5/public
// Manages subscriptions, auto-reconnect with 3s backoff, and price event dispatch.

const WS_URL = "wss://ws.okx.com:8443/ws/v5/public";
const RECONNECT_DELAY_MS = 3000;

type PriceCallback = (price: number) => void;
export type WsStatusValue = "connected" | "connecting" | "disconnected";
type StatusCallback = (status: WsStatusValue) => void;

// OKX ticker message shape (only fields we use)
interface OkxTickerData {
  instId: string;
  last: string;
}
interface OkxMessage {
  arg?: { channel: string; instId: string };
  data?: OkxTickerData[];
}

// Convert ccxt-style symbol → OKX instId
// e.g. "BTC/USDT:USDT" → "BTC-USDT-SWAP"
function symbolToInstId(symbol: string): string {
  // "BTC/USDT:USDT" → strip ":USDT" margin suffix, replace "/" with "-", append "-SWAP"
  const base = symbol.replace(/:[^:]+$/, "").replace("/", "-");
  return `${base}-SWAP`;
}

// Convert OKX instId → ccxt-style symbol
// e.g. "BTC-USDT-SWAP" → "BTC/USDT:USDT"
function instIdToSymbol(instId: string): string {
  // "BTC-USDT-SWAP" → remove "-SWAP", split on "-", rejoin as "BTC/USDT:USDT"
  const withoutSwap = instId.replace(/-SWAP$/, "");
  const [base, quote] = withoutSwap.split("-");
  return `${base}/${quote}:${quote}`;
}

class OkxWsClient {
  private ws: WebSocket | null = null;
  private listeners: Map<string, Set<PriceCallback>> = new Map();
  // Reference counts per symbol so we unsubscribe only when all consumers are gone
  private refCounts: Map<string, number> = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyClosed = false;
  private statusCallbacks: Set<StatusCallback> = new Set();
  private currentStatus: WsStatusValue = "disconnected";

  private emitStatus(s: WsStatusValue) {
    this.currentStatus = s;
    for (const cb of this.statusCallbacks) cb(s);
  }

  /** Register a callback that fires whenever WS connection status changes.
   *  Returns an unsubscribe function. */
  onStatusChange(cb: StatusCallback): () => void {
    this.statusCallbacks.add(cb);
    cb(this.currentStatus); // emit current immediately
    return () => { this.statusCallbacks.delete(cb); };
  }

  private connect() {
    this.intentionallyClosed = false;
    this.emitStatus("connecting");
    const ws = new WebSocket(WS_URL);
    this.ws = ws;

    ws.onopen = () => {
      this.emitStatus("connected");
      // Re-subscribe to all active symbols after reconnect
      for (const symbol of this.listeners.keys()) {
        this.sendSubscribe(symbol);
      }
    };

    ws.onmessage = (event: MessageEvent<string>) => {
      let msg: OkxMessage;
      try {
        msg = JSON.parse(event.data) as OkxMessage;
      } catch {
        return;
      }
      if (!msg.data?.length || msg.arg?.channel !== "tickers") return;

      for (const d of msg.data) {
        const price = parseFloat(d.last);
        if (!isFinite(price)) continue;
        const symbol = instIdToSymbol(d.instId);
        const cbs = this.listeners.get(symbol);
        if (cbs) {
          for (const cb of cbs) cb(price);
        }
      }
    };

    ws.onclose = () => {
      this.emitStatus("disconnected");
      if (!this.intentionallyClosed) {
        this.scheduleReconnect();
      }
    };

    ws.onerror = () => {
      // onclose fires after onerror; reconnect handled there
      ws.close();
    };
  }

  private scheduleReconnect() {
    if (this.reconnectTimer !== null) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.listeners.size > 0) this.connect();
    }, RECONNECT_DELAY_MS);
  }

  private sendSubscribe(symbol: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          op: "subscribe",
          args: [{ channel: "tickers", instId: symbolToInstId(symbol) }],
        })
      );
    }
  }

  private sendUnsubscribe(symbol: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          op: "unsubscribe",
          args: [{ channel: "tickers", instId: symbolToInstId(symbol) }],
        })
      );
    }
  }

  subscribe(symbol: string, cb: PriceCallback) {
    const count = this.refCounts.get(symbol) ?? 0;
    this.refCounts.set(symbol, count + 1);

    if (!this.listeners.has(symbol)) {
      this.listeners.set(symbol, new Set());
    }
    this.listeners.get(symbol)!.add(cb);

    // First subscriber for this symbol — connect if needed and subscribe
    if (count === 0) {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        this.connect();
      } else {
        this.sendSubscribe(symbol);
      }
    }
  }

  unsubscribe(symbol: string, cb: PriceCallback) {
    const cbs = this.listeners.get(symbol);
    if (cbs) {
      cbs.delete(cb);
    }

    const count = (this.refCounts.get(symbol) ?? 1) - 1;
    if (count <= 0) {
      this.refCounts.delete(symbol);
      this.listeners.delete(symbol);
      this.sendUnsubscribe(symbol);

      // No active subscriptions — close the socket cleanly
      if (this.listeners.size === 0 && this.ws) {
        this.intentionallyClosed = true;
        this.ws.close();
        this.ws = null;
      }
    } else {
      this.refCounts.set(symbol, count);
    }
  }
}

// Module-level singleton
export const okxWs = new OkxWsClient();
